import { DownOutlined } from "@ant-design/icons";
import { Dropdown, Pagination as AntPagination, Typography, Flex } from "antd";
import { memo, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { ListStoreConstructor } from "stores/ListStoreConstructor";

import styles from "./pagination.module.css";

interface PaginationProps {
  store: ListStoreConstructor<any>;
  /**
   * Элемент, к которому будет скролл при изменении
   */
  scrollRef?: React.RefObject<HTMLElement>;
  /**
   * Массив с вариантами количества элементов
   * [10, 20, 50, 100] по умолчанию
   */
  pageSizeMenuItems?: number[];
  /**
   * Показывать селектор количества элементов
   */
  showPageSizeChanger?: boolean;
  /**
   * Работает только если showPageSizeChanger: false
   */
  alignPagination?: "center" | "end" | "start";
}

const PageSizeMenu = memo(
  ({
    pageSize,
    pageSizeOptions,
    pageSizeDropdownVisible,
    onOpenChange,
    onSizeChange,
  }: {
    pageSize: number;
    pageSizeOptions: number[];
    pageSizeDropdownVisible: boolean;
    onOpenChange: (visible: boolean) => void;
    onSizeChange: (value: number) => void;
  }) => {
    const items = useMemo(
      () =>
        pageSizeOptions.map((size) => ({
          key: size.toString(),
          label: size,
          onClick: () => onSizeChange(size),
        })),
      [pageSizeOptions, onSizeChange]
    );

    return (
      <div className={styles.pageSizeChanger}>
        <Dropdown menu={{ items }} trigger={["click"]} open={pageSizeDropdownVisible} onOpenChange={onOpenChange}>
          <div className={styles.fullClickableArea}>
            <Typography.Text strong>Показывать по </Typography.Text>
            <Typography.Text strong className={styles.valueText}>
              {pageSize}
            </Typography.Text>
            <DownOutlined className={styles.arrowIcon} />
          </div>
        </Dropdown>
      </div>
    );
  }
);

export const UiPagination = observer(
  ({
    store,
    scrollRef,
    pageSizeMenuItems = [10, 20, 50, 100],
    showPageSizeChanger = true,
    alignPagination,
  }: PaginationProps) => {
    const [pageSizeDropdownVisible, setPageSizeDropdownVisible] = useState(false);

    const { paginationData } = store;
    const currentPage = paginationData.currentPage || 1;
    const pageSize = paginationData.pageSize || 10;
    const total = paginationData.totalElements || 0;

    const handlePageChange = (page: number) => {
      store.onChangeDataFlow({
        paginationData: {
          currentPage: page,
          pageSize,
        },
        filters: store.filters,
        sorters: store.sorters,
      });

      scrollRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handlePageSizeChange = (value: number) => {
      store.onChangeDataFlow({
        paginationData: {
          currentPage: 1,
          pageSize: value,
        },
        filters: store.filters,
        sorters: store.sorters,
      });

      scrollRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handlePageSizeVisibleChange = (visible: boolean) => {
      setPageSizeDropdownVisible(visible);
    };

    return showPageSizeChanger ? (
      <Flex justify="space-between" align="center">
        <PageSizeMenu
          pageSize={pageSize}
          pageSizeOptions={pageSizeMenuItems}
          pageSizeDropdownVisible={pageSizeDropdownVisible}
          onOpenChange={handlePageSizeVisibleChange}
          onSizeChange={handlePageSizeChange}
        />

        <AntPagination
          current={currentPage}
          total={total}
          pageSize={pageSize}
          onChange={handlePageChange}
          showSizeChanger={false}
        />
      </Flex>
    ) : (
      <AntPagination
        current={currentPage}
        total={total}
        pageSize={pageSize}
        onChange={handlePageChange}
        showSizeChanger={false}
        align={alignPagination}
      />
    );
  }
);
